import { useQuery, useQueryClient } from "@tanstack/react-query";
import { createFileRoute, redirect, useNavigate } from "@tanstack/react-router";
import { motion } from "framer-motion";
import { toast } from "sonner";

import { ApprovalActions } from "@/components/approvals/ApprovalActions";
import { AuditScorePanel } from "@/components/approvals/AuditScorePanel";
import { DraftPreview } from "@/components/approvals/DraftPreview";
import { api } from "@/lib/api";
import { formatRelativeTime } from "@/lib/formatters";
import { isAuthenticated, pb } from "@/lib/pb";

interface PendingPostDetail {
  id: string;
  run_id: string;
  slug: string;
  title_es: string;
  title_en: string;
  category: string;
  status: string;
  audit_score: number | null;
  audit_json: Record<string, unknown> | null;
  draft_html: string;
  created: string;
}

export const Route = createFileRoute("/approvals/$id")({
  beforeLoad: () => {
    if (!isAuthenticated()) throw redirect({ to: "/login" });
  },
  component: ApprovalDetail,
});

function ApprovalDetail() {
  const { id } = Route.useParams();
  const navigate = useNavigate();
  const qc = useQueryClient();

  const { data, isLoading, error } = useQuery({
    queryKey: ["pending_post", id],
    queryFn: () => pb.collection("pending_posts").getOne<PendingPostDetail>(id),
  });

  async function handleAction(kind: "approve" | "regenerate" | "reject") {
    const labels = {
      approve: "aprobando y publicando…",
      regenerate: "regenerando borrador…",
      reject: "descartando…",
    };
    const promise = api[kind](id).then(() => {
      qc.invalidateQueries({ queryKey: ["pending_posts"] });
      qc.invalidateQueries({ queryKey: ["pending_post", id] });
      setTimeout(() => navigate({ to: "/approvals" }), 800);
    });
    toast.promise(promise, {
      loading: labels[kind],
      success: kind === "approve" ? "✓ publicado" : kind === "regenerate" ? "✓ regenerado" : "✓ descartado",
      error: (e) => `× ${e.message}`,
    });
  }

  if (isLoading) return <div className="text-muted">cargando…</div>;
  if (error || !data) return <div className="text-danger">error: {(error as Error)?.message ?? "not found"}</div>;

  return (
    <motion.div
      initial={{ opacity: 0, y: 12 }}
      animate={{ opacity: 1, y: 0 }}
      className="max-w-6xl mx-auto space-y-6"
    >
      <div className="flex items-start justify-between gap-6 flex-wrap">
        <div className="min-w-0">
          <p className="path-chip">/approvals/{data.slug}</p>
          <h1 className="text-2xl font-semibold mt-1 text-balance">{data.title_es}</h1>
          <p className="text-sm text-muted mt-2 font-mono">
            {data.category} · run {data.run_id} · {formatRelativeTime(data.created)}
          </p>
        </div>
        <ApprovalActions
          onApprove={() => handleAction("approve")}
          onRegenerate={() => handleAction("regenerate")}
          onReject={() => handleAction("reject")}
          disabled={data.status !== "pending" && data.status !== "failed"}
        />
      </div>

      <div className="grid lg:grid-cols-[1fr_320px] gap-6">
        <DraftPreview html={data.draft_html} />
        <AuditScorePanel score={data.audit_score} audit={data.audit_json} />
      </div>
    </motion.div>
  );
}
